/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { RichText, RichTextComponent } from '@msdyn365-commerce/core';
import { CommerceProperty } from '@msdyn365-commerce/retail-proxy';
import debounce from 'lodash/debounce';
import { observable, reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { SubscriptionCommerceValues } from '../../common/subscription-commerce.values';
import { getSubscriptionValues, updateSubscription } from '../../Utilities/subscription-manager';
import { ISubscriptionBoxData } from './subscription-box.data';
import { IFrequencyTableData, ISubscriptionBoxProps } from './subscription-box.props.autogenerated';

export interface ISubscriptionBoxViewProps extends ISubscriptionBoxProps<ISubscriptionBoxData> { }

export interface ISubscriptionBoxViewState {
    boxName: string;
}

/**
 *
 * SubscriptionBox component
 * @extends {React.Component<ISubscriptionBoxProps<ISubscriptionBoxData>>}
 */
@observer
class SubscriptionBox extends React.Component<ISubscriptionBoxProps<ISubscriptionBoxData>, ISubscriptionBoxViewState> {

    /**
     * stores the user's typed name
     */
    @observable private _subscriptionBoxName: string = '';

    /**
     * stores the user's selected frequency, gets defaulted in constructor
     */
    @observable private _selectedFrequency: IFrequencyTableData | undefined;

    constructor(props: ISubscriptionBoxProps<ISubscriptionBoxData>) {
        super(props);

        // debounce function now has typings and requires an intermediate function to use properly.
        // implement later. new typings declare DebouncedFunc<() => Promise<void>> not () => Promise<void>
        this._publishNameToCart = debounce(this._publishNameToCart.bind(this), 500) as unknown as () => Promise<void>;
        this._onNameChange = this._onNameChange.bind(this);
        this._onFrequencyChange = this._onFrequencyChange.bind(this);
        this._submitSubscriptionToCart = this._submitSubscriptionToCart.bind(this);

        reaction(
            () => [this.props.data.cart?.result],
            () => {
                const values = getSubscriptionValues(this.props.data.cart.result?.cart);
                this._subscriptionBoxName = values.name!;
                if (values.name && this.props.config.allSubscriptionsLink && this.props.config.allSubscriptionsLink.destinationUrl) {
                    window.location.replace(this.props.config.allSubscriptionsLink.destinationUrl);
                }
            }
        );
    }

    public render(): JSX.Element {
        if (!this.props.data.cart?.result) { return <div />; }
        const { subscriptionBox__name, subscriptionBox__delivery } = this.props.resources;
        const { className, headerText } = this.props.config;
        const values = getSubscriptionValues(this.props.data.cart.result.cart);
        return (
            <div className={`subscription-box ${className || ''}`}>
                {headerText && this._optionalRichTextRender(headerText, 'subscription-box-header')}
                <div className='subscription-box-options'>
                    <div className='subscription-box-name-group'>
                        <label className='subscription-box-name-label'>{subscriptionBox__name}</label>
                        <input
                            type='text'
                            className='subscription-box-name-input'
                            onChange={this._onNameChange}
                            value={this._subscriptionBoxName}
                            placeholder={SubscriptionCommerceValues.SUBSCRIPTION_DEFAULT_NAME}
                        />
                    </div>
                    <div className='subscription-box-delivery-group'>
                        <label className='subscription-box-delivery-label'>{subscriptionBox__delivery}</label>
                        {this._frequencyTable(values.frequency)}
                    </div>
                </div>
            </div>
        );
    }

    /**
     * optionally renders richtext if the richtext exists, just a utility function for
     * not having to repeat conditionals all over the place
     *
     * @param richText rich text to render
     * @param greyText if the text is to be greyed, reference mock as to why
     */
    private _optionalRichTextRender(richText: RichText, className?: string): JSX.Element | null {
        return <RichTextComponent text={richText} className={className || ''} />;
    }

    /**
     * renders the JSX for the frequency table
     */
    private _frequencyTable(initialId?: string): JSX.Element {
        const { frequencyTable } = this.props.config;

        let firstId: string | undefined;
        if (!initialId && this._selectedFrequency) {
            firstId = this.props.config.frequencyTable?.find(freq => !!freq)?.id;
        }
        return (
            <select className='subscription-box-select' value={this._selectedFrequency?.id || initialId || firstId} onChange={this._onFrequencyChange} >
                {
                    frequencyTable?.map(
                        frequency => (
                            <option
                                key={frequency.id}
                                value={frequency.id}
                                aria-selected={
                                    (frequency.id === this._selectedFrequency?.id) ||
                                    (initialId === frequency.id) ||
                                    (firstId === frequency.id)
                                }
                            >
                                {frequency.label}
                            </option>
                        )
                    )
                }
            </select>
        );
    }

    private async _submitSubscriptionToCart({ freq, name }: { freq?: boolean; name?: boolean }): Promise<void> {

        // we need to parse this form's format into how the cart likes to
        // handle things
        const cartChanges: CommerceProperty[] = [];

        // start frequency attempt with the selected frequency
        let selectedFrequency: string | undefined = this._selectedFrequency?.id;

        // if they are submitting without touching, attempt to grab their current one in session
        if (!selectedFrequency) {
            selectedFrequency = getSubscriptionValues(this.props.data.cart.result?.cart).frequency;
        }

        // if they didn't have one in session and this is their submit attempt,
        // use the top level frequency. this should always be in sync with what they see as
        // they have never touched the dropdown if they reach here
        if (!selectedFrequency) {
            selectedFrequency = this.props.config.frequencyTable?.find(v => !!v)!.id;
        }

        cartChanges.push({
            Key: SubscriptionCommerceValues.SUBSCRIPTION_FREQUENCY,
            Value: {
                StringValue: selectedFrequency
            }
        });

        cartChanges.push({
            Key: SubscriptionCommerceValues.SUBSCRIPTION_NAME,
            Value: {
                StringValue: this._subscriptionBoxName
            }
        });

        await updateSubscription(this.props.data.cart, cartChanges, this.props.context);
        return;
    }

    private async _publishNameToCart(): Promise<void> {
        return this._submitSubscriptionToCart({ name: true });
    }

    /**
     * change event for selecting a new frequency
     */
    private async _onFrequencyChange(event: React.ChangeEvent<HTMLSelectElement>): Promise<void> {
        this._selectedFrequency = this.props.config.frequencyTable?.find(frequncy => frequncy.id === event.target.value);
        return this._submitSubscriptionToCart({ freq: true });
    }

    /**
     * change event for the subscription name
     */
    private async _onNameChange(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
        this._subscriptionBoxName = event.target.value;
        return this._publishNameToCart();
    }
}

export default SubscriptionBox;